import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubformField } from '@shared/data/entity/fields/SubformField';
import { useBatch } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, RawFieldData, UpdateField } from '@shared/scenes/entity/types';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { useEntityFormViewTemplate } from '@shared/scenes/entity/util/useEntityFormViewTemplate';
import {
  applyComponentOverrides,
  useViewComponents,
} from '@shared/scenes/entity/util/useViewComponents';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { useCreateEntityFields } from './useCreateEntityFields';
import { useSaveNewEntity } from './useSaveNewCardEntity';

type Status = 'loading' | 'error' | 'input' | 'success';

interface CreateCardEntity {
  components: API.AnyComponent[];
  fields: FieldRecord | null;
  errors: CardEntityError[];
  viewTemplateTitle: string;
  viewTemplate?: API.ViewTemplateFragment;
  updateField: UpdateField;
  onPressSave: () => void;
  isLoading: boolean;
  status: Status;
  setStatus: (status: Status) => void;
  isDirty: boolean;
  resetForm: () => void;
  savedEntity?: API.WorkflowEntity;
  /** True once save has been attempted while the form has errors. Used by
   *  SubformNavigationContext to trigger per-subform error display. Resets when the form
   *  becomes fully valid (including subforms). */
  saveAttempted: boolean;
}

interface UseCreateCardEntityProps {
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  queryFieldOverrides?: RawFieldData;
  /**
   * When true, hold off seeding the form until the source card for a duplicate
   * has loaded. See {@link useCreateEntityFields}.
   */
  isDuplicateLoading?: boolean;
}

export function useCreateCardEntity({
  workflowTemplateId,
  viewTemplateId,
  queryFieldOverrides,
  isDuplicateLoading = false,
}: UseCreateCardEntityProps): CreateCardEntity {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const viewTemplate = useEntityFormViewTemplate({
    workflowTemplateId,
    viewTemplateId,
    type: 'CREATE',
  });
  const [status, setStatus] = useState<Status>('loading');
  const [saveAttemptedWithLocalErrors, setSaveAttemptedWithLocalErrors] = useState(false);
  const [batchCreateError, setBatchCreateError] = useState<any>(null);
  const [savedEntity, setSavedEntity] = useState<API.WorkflowEntity>();
  const { shouldBatchCreate, batchCreate } = useBatch();
  const { fields, originalFields, updateField, isDirty, resetFields } = useCreateEntityFields({
    workflowTemplate,
    viewTemplate,
    queryFieldOverrides,
    isDuplicateLoading,
  });
  // Hold fields as null until defaults have been applied, so downstream components
  // (e.g. CardEntityCreateFormContent) wait to mount field editors until data is ready.
  // Without this, field components mount with empty state and miss late-arriving values
  // like fromEntity overrides used by card duplication.
  const initializedFields = isNil(originalFields) ? null : fields;

  const {
    saveEntity,
    loading: saveEntityLoading,
    errors: apiErrors,
    // Pass the resolved view template's id (not the raw route slug prop, which is
    // nil when the create form resolves its template by behavior/exclusion). Without
    // it the server skips all component required-ness checks. Mirrors the edit path
    // in useEditCardEntity, which passes viewTemplate?.id.
  } = useSaveNewEntity(workflowTemplateId, viewTemplate?.id);

  const components = useViewComponents({
    workflowTemplate,
    viewTemplate,
    includeServerManagedComponents: false,
    includeInputComponentsOnly: true,
  });

  // The full component set, overrides applied and nothing filtered out — it keeps the managed DATE
  // children a DATE_RANGE owns, which `components` drops. Resolves required-ness for managing
  // components. Fields below the form read the same set from WorkflowComponentsContext.
  const workflowComponents = useMemo(
    () => applyComponentOverrides(workflowTemplate?.components ?? [], viewTemplate),
    [workflowTemplate?.components, viewTemplate],
  );

  const { errors, isValid } = useFormErrors({
    apiErrors: batchCreateError ? [batchCreateError] : apiErrors,
    fields,
    components,
    workflowComponents,
    includeOnSubmitErrors: saveAttemptedWithLocalErrors,
    includeLocalErrors: saveAttemptedWithLocalErrors,
  });

  // Subform internal required errors are not tracked in useFormErrors (they belong to
  // SubformErrorContext for per-component display control). Block the mutation directly.
  const hasSubformRequiredErrors = useMemo(
    () =>
      components.some(component => {
        const field = fields?.[component.id];
        return field instanceof SubformField && field.hasUnfilledRequiredFields();
      }),
    [components, fields],
  );

  useEffect(() => {
    setStatus(prevStatus => {
      if (prevStatus === 'success') {
        return prevStatus;
      }
      const isFieldsReady = !isNil(initializedFields);
      const isViewTemplateReady = !isNil(viewTemplate);
      if (saveEntityLoading || !isFieldsReady || !isViewTemplateReady) {
        return 'loading';
      } else if (!isEmpty(errors)) {
        return 'error';
      } else {
        return 'input';
      }
    });
  }, [errors, saveEntityLoading, status, initializedFields, viewTemplate]);

  useEffect(() => {
    if (isValid && !hasSubformRequiredErrors) {
      setSaveAttemptedWithLocalErrors(false);
    }
  }, [isValid, hasSubformRequiredErrors]);

  const save = useCallback(async () => {
    if (!isValid || hasSubformRequiredErrors) {
      setSaveAttemptedWithLocalErrors(true);
      return;
    }
    setSaveAttemptedWithLocalErrors(false);

    if (shouldBatchCreate) {
      try {
        const result = await batchCreate({
          workflowTemplateId: workflowTemplateId ?? '',
          fields: toFieldJson(fields, workflowTemplate),
        });
        const entities = result?.data?.createRecurringEntities?.workflowEntitiesResult?.entities;
        const entity = entities?.[0];
        if (!entity) {
          throw new Error('No entity returned from batch create');
        }
        setStatus('success');
        setSavedEntity(entity);
      } catch (error) {
        setBatchCreateError(error);
        setStatus('error');
      }
    } else {
      try {
        const result = await saveEntity(fields);
        const entity = result.data?.createWorkflowEntity?.entity;
        if (!entity) {
          throw new Error('No entity returned from saveEntity');
        }
        setStatus('success');
        setSavedEntity(entity);
      } catch (error) {
        setStatus('error');
      }
    }
  }, [
    fields,
    saveEntity,
    isValid,
    hasSubformRequiredErrors,
    shouldBatchCreate,
    batchCreate,
    workflowTemplateId,
    workflowTemplate,
  ]);

  const resetForm = useCallback(() => {
    resetFields();
    setStatus('input');
  }, [resetFields, setStatus]);

  return {
    components,
    fields: initializedFields,
    errors,
    viewTemplateTitle: viewTemplate?.name ?? '',
    viewTemplate,
    updateField,
    onPressSave: save,
    isLoading: status === 'loading',
    status,
    isDirty,
    setStatus,
    resetForm,
    savedEntity,
    saveAttempted: saveAttemptedWithLocalErrors,
  };
}
